import katex from 'katex';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

import { renderMarkdownCodeBlockHtml } from './markdown-code-highlight';
import { MARKDOWN_EMOJI_SHORTCODE_MAP } from './markdown-emoji-shortcodes';

export interface MarkdownRenderResult {
  html: string;
  katexError: string | null;
}

export type MarkdownImagePolicy =
  'external-https-only' | 'external-https-and-app-assets' | 'allow-relative-and-https';

export interface MarkdownRenderOptions {
  imagePolicy?: MarkdownImagePolicy;
  /**
   * Niedrigste Überschriftenebene für ein Markdown-`#`.
   * Eingebettete Inhalte starten meist bei 2+, damit sie die Seiten-H1 nicht duplizieren.
   * Ausnahme: Legal-Markdown mit `1`, wenn die führende `#`-Überschrift ohnehin entfernt wird.
   */
  headingStartLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * Kurzlabels (Antworten, Ordering-/Matching-Elemente): führende Listenmarker escapen,
   * damit „2. Schritt“ nicht als einzeilige `<ol>` mit Anzeige „1.“ landet.
   */
  escapeListMarkers?: boolean;
}

const MARKDOWN_EMOJI_SHORTCODES = new Map(Object.entries(MARKDOWN_EMOJI_SHORTCODE_MAP));
const INTERNAL_MARKDOWN_LINK_HOSTNAMES = new Set([
  'arsnova.eu',
  'www.arsnova.eu',
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  '[::1]',
]);

const EMOJI_SHORTCODE_PATTERN = /:([a-z0-9_+-]+):/gi;
const EXTERNAL_LINK_SVG =
  '<svg class="markdown-external-link-icon__svg" viewBox="0 0 16 16" focusable="false" aria-hidden="true"><path d="M9.5 2.5h4v4" /><path d="M8.5 7.5 13.5 2.5" /><path d="M13.5 8.5v3a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" /></svg>';

/**
 * Bereinigt typische Nutzer-/Escape-Artefakte vor KaTeX.
 * `\\n` (Backslash + „n“) ist in KaTeX keine gültige Sequenz und erzeugt „Undefined control sequence: \\n“.
 * Commands wie `\\neq`, `\\nabla`, `\\not`, `\\newline` bleiben erhalten (nach `\\n` folgt ein Buchstabe).
 */
function normalizeMathExpressionBeforeKatex(raw: string): string {
  let s = raw.trim();
  s = s.replace(/\\n(?![a-zA-Z])/g, ' ');
  return s.trim();
}

/**
 * Toleriert typische Eingabefehler wie `**text **` oder `_text _` (Leerzeichen vor schließenden Delimitern),
 * die nach striktem CommonMark-Standard das Rendering von Fett/Kursiv unterbinden.
 * Sämtliche Code-Formen (Backtick/Tilde-Fences ```/~~~, eingerückte Codeblöcke und Inline-Code) bleiben unverändert.
 */
export function normalizeEmphasisWhitespace(raw: string): string {
  if (!raw || (!raw.includes('**') && !raw.includes('__'))) return raw;

  const parts = raw.split(
    /(```[\s\S]*?```|~~~[\s\S]*?~~~|`+[^`\n]+`+|(?:(?:^|\r?\n)(?:\r?\n)(?: {4}|\t)[^\n]*(?:\r?\n|$))+)/gm,
  );
  return parts
    .map((part, index) => {
      if (index % 2 === 1) {
        return part;
      }
      return part
        .replace(/(^|[\s([{>])\*\*([^\s*\n](?:[^\n*]*?[^\s*\n])?)\s+\*\*/gm, '$1**$2** ')
        .replace(/(^|[\s([{>])__([^\s_\n](?:[^\n_]*?[^\s_\n])?)\s+__/gm, '$1__$2__ ');
    })
    .join('');
}

/**
 * Verhindert, dass kurze Labels wie „2. Translation“ oder „9. November 1918“
 * als eigenständige Markdown-Liste gerendert werden (Browser zeigt dann oft „1.“).
 */
export function escapeLeadingMarkdownListMarkers(source: string): string {
  if (!source) return source;
  return source
    .replace(/^(\s*)(\d+)\.(\s+)/gm, '$1$2\\.$3')
    .replace(/^(\s*)([*+-])(\s+)/gm, '$1\\$2$3');
}

/**
 * Früher: entfernte führende Ordnungsnummern. Das zerstörte legitime Texte wie „9. November“.
 * Beibehalten als No-Op für Aufrufer; für List-Rendering `escapeLeadingMarkdownListMarkers` nutzen.
 */
export function stripLeadingOrderedListLabel(text: string): string {
  return text;
}

export function renderMarkdownWithKatex(
  source = '',
  options?: MarkdownRenderOptions,
): MarkdownRenderResult {
  const prepared = options?.escapeListMarkers ? escapeLeadingMarkdownListMarkers(source) : source;
  const input = normalizeEmphasisWhitespace(prepared);
  let katexError: string | null = null;
  const renderedMath: string[] = [];

  const storeRenderedMath = (html: string): string => {
    const index = renderedMath.push(html) - 1;
    return mathPlaceholder(index);
  };

  const renderExpression = (expression: string, displayMode: boolean): string => {
    const normalized = normalizeMathExpressionBeforeKatex(expression);
    try {
      return storeRenderedMath(
        katex.renderToString(normalized, {
          displayMode,
          throwOnError: true,
          strict: 'warn',
          trust: false,
          maxExpand: 1000,
          maxSize: 20,
        }),
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : $localize`Ungültige KaTeX-Syntax.`;
      const katexErrorLabel = $localize`KaTeX-Fehler`;
      if (!katexError) {
        katexError = message;
      }
      return storeRenderedMath(
        `<span class="markdown-katex-error">${katexErrorLabel}: ${escapeHtml(message)}</span>`,
      );
    }
  };

  const withBlockMath = input.replaceAll(/\$\$([\s\S]+?)\$\$/g, (_, expression: string) =>
    renderExpression(expression, true),
  );
  const withInlineMath = withBlockMath.replaceAll(/\$([^$\n]+?)\$/g, (_, expression: string) =>
    renderExpression(expression, false),
  );

  const markdownHtml = parseMarkdownEscapingInlineHtml(withInlineMath, {
    // Quiz-/Session-Inhalte sollen standardmäßig nur HTTPS-Bilder akzeptieren.
    // Relative Asset-Pfade bleiben eine explizite Ausnahme für Demo-/Systeminhalte;
    // im lockeren Modus sind in Dev zusätzlich Loopback-HTTP-Bilder erlaubt.
    imagePolicy: options?.imagePolicy ?? 'external-https-only',
    headingStartLevel: options?.headingStartLevel ?? 2,
  });
  const html = renderedMath.reduce(
    (current, value, index) => current.replaceAll(mathPlaceholder(index), value),
    markdownHtml,
  );
  return { html: sanitizeMarkdownHtml(html), katexError };
}

function parseMarkdownEscapingInlineHtml(
  source: string,
  options: Required<Pick<MarkdownRenderOptions, 'imagePolicy' | 'headingStartLevel'>>,
): string {
  const renderer = new marked.Renderer();
  const shallowestHeadingDepth = marked.lexer(source).reduce<number | null>((minimum, token) => {
    if (token.type !== 'heading') {
      return minimum;
    }
    return minimum === null ? token.depth : Math.min(minimum, token.depth);
  }, null);
  const headingOffset =
    shallowestHeadingDepth === null ? 0 : options.headingStartLevel - shallowestHeadingDepth;
  renderer.code = (token) => renderMarkdownCodeBlockHtml(token);
  renderer.html = ({ text }) => renderTrustedInlineHtml(text);
  renderer.heading = function ({ tokens, depth }): string {
    const level = Math.min(6, Math.max(options.headingStartLevel, depth + headingOffset));
    return `<h${level}>${this.parser.parseInline(tokens)}</h${level}>\n`;
  };
  renderer.text = function (token): string {
    const inlineTokens = 'tokens' in token ? token.tokens : undefined;
    if (inlineTokens?.length) {
      return this.parser.parseInline(inlineTokens);
    }
    return renderMarkdownText(token.text);
  };
  renderer.link = ({ href, title, text }): string => {
    const safeHref = sanitizeMarkdownUrl(href, 'link');
    if (!safeHref) {
      return text;
    }
    const hrefEsc = escapeHtml(safeHref);
    const hasTitle = title !== undefined && title !== null && String(title).trim() !== '';
    const renderedText = looksLikeRenderedHtml(text) ? text : renderMarkdownText(text);
    const isExternal = isExternalHttpsMarkdownUrl(safeHref);
    const isAppAsset = isAppAssetMarkdownUrl(safeHref);
    const linkTitle = hasTitle ? String(title).trim() : '';
    const externalLinkHint = getExternalLinkHint();
    const effectiveTitle = buildMarkdownLinkTitle(linkTitle, isExternal ? externalLinkHint : '');
    const titleAttr = effectiveTitle ? ` title="${escapeHtml(effectiveTitle)}"` : '';
    const privacyAttrs = isExternal
      ? ' target="_blank" rel="noopener noreferrer" referrerpolicy="no-referrer" data-markdown-link-kind="external"'
      : isAppAsset
        ? ' target="_blank" rel="noopener noreferrer" data-markdown-link-kind="asset"'
        : '';
    const externalIcon = isExternal
      ? `<span class="markdown-external-link-icon" aria-hidden="true">${EXTERNAL_LINK_SVG}</span><span class="sr-only">${escapeHtml(externalLinkHint)}</span>`
      : '';
    const classAttr = isAppAsset ? ' class="markdown-inline-link"' : '';
    return `<a href="${hrefEsc}"${classAttr}${titleAttr}${privacyAttrs}>${renderedText}${externalIcon}</a>`;
  };
  /** `alt` allein löst keinen Hover-Tooltip aus; `title` schon (optional explizit in `![](url "title")`). */
  renderer.image = ({ href, title, text }): string => {
    const safeHref = sanitizeMarkdownUrl(href, 'image', options.imagePolicy);
    if (!safeHref) {
      return escapeHtml(text);
    }
    const hrefEsc = escapeHtml(safeHref);
    const altEsc = escapeHtml(text);
    const hasTitle = title !== undefined && title !== null && String(title).trim() !== '';
    const tooltip = hasTitle ? String(title).trim() : text;
    const titleEsc = escapeHtml(tooltip);
    return `<img src="${hrefEsc}" alt="${altEsc}" title="${titleEsc}" loading="eager" decoding="async" crossorigin="anonymous" referrerpolicy="no-referrer" data-markdown-image-lightbox="true" data-markdown-image-state="loading" />`;
  };
  return marked.parse(source, { renderer }) as string;
}

function renderTrustedInlineHtml(text: string): string {
  const value = text.trim();
  if (/^<br\s*\/?>$/i.test(value)) {
    return value;
  }
  if (/^<\/?(?:strong|b|em|i|code|sub|sup)>$/i.test(value)) {
    return value;
  }
  return escapeHtml(text);
}

/** Markdown → HTML ohne KaTeX (kein `$…$`-Parsing). Für lange System-Prompts mit JSON-Beispielen. */
export function renderMarkdownWithoutKatex(source = '', options?: MarkdownRenderOptions): string {
  return sanitizeMarkdownHtml(
    parseMarkdownEscapingInlineHtml(source, {
      imagePolicy: options?.imagePolicy ?? 'allow-relative-and-https',
      headingStartLevel: options?.headingStartLevel ?? 2,
    }),
  );
}

/**
 * Ersetzt MOTD-Medien unter `/assets/...` oder `assets/...` (Bilder und Links) durch absolute URLs
 * relativ zur aktuellen Build-Basis (z. B. `https://arsnova.eu/de/`). Das hält lokalisierte Builds
 * und Admin-Preview konsistent.
 */
export function absolutizeMarkdownHtmlRootAssetImgSrc(html: string, origin: string): string {
  const base = origin.replace(/\/$/, '');
  if (!base) return html;
  return html
    .replaceAll(/src="\/(assets\/[^"]+)"/g, `src="${base}/$1"`)
    .replaceAll(/src="(assets\/[^"]+)"/g, `src="${base}/$1"`)
    .replaceAll(/href="\/(assets\/[^"]+)"/g, `href="${base}/$1"`)
    .replaceAll(/href="(assets\/[^"]+)"/g, `href="${base}/$1"`);
}

/**
 * Hängt `cv=&lt;contentVersion&gt;` an MOTD-Bilder unter `/assets/…` an.
 * Der PWA-Service-Worker cached `assets/**` — gleiche URL kann nach Updates einen veralteten
 * Cache-Treffer liefern; neues `contentVersion` (z. B. nach Admin-Speichern) erzwingt einen frischen Fetch.
 */
export function appendMotdContentVersionToAssetImgSrc(
  html: string,
  contentVersion: number,
): string {
  const cv = String(contentVersion);
  const bump = (url: string): string => {
    if (/[?&]cv=\d+/.test(url)) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}cv=${cv}`;
  };
  let out = html.replaceAll(/src="(https?:\/\/[^"']+\/assets\/[^"']+)"/gi, (_full, url: string) => {
    return `src="${bump(url)}"`;
  });
  out = out.replaceAll(/src="(\/assets\/[^"']+)"/gi, (_full, path: string) => {
    return `src="${bump(path)}"`;
  });
  return out;
}

function mathPlaceholder(index: number): string {
  return `KATEXPLACEHOLDERTOKEN${index}`;
}

function getExternalLinkHint(): string {
  return $localize`:@@markdown.externalLink:Externer Link: öffnet eine andere Website`;
}

function buildMarkdownLinkTitle(baseTitle: string, externalHint: string): string {
  return [baseTitle, externalHint].filter(Boolean).join(' · ');
}

function renderMarkdownText(value: string): string {
  let html = '';
  let lastIndex = 0;
  for (const match of value.matchAll(EMOJI_SHORTCODE_PATTERN)) {
    const full = match[0];
    const code = match[1];
    const index = match.index ?? 0;
    const emoji = MARKDOWN_EMOJI_SHORTCODES.get(code.toLowerCase());
    if (!emoji) {
      continue;
    }

    html += escapeHtml(value.slice(lastIndex, index));
    html += `<span class="markdown-emoji" title="${escapeHtml(full)}">${emoji}</span>`;
    lastIndex = index + full.length;
  }

  html += escapeHtml(value.slice(lastIndex));
  return html;
}

function looksLikeRenderedHtml(value: string): boolean {
  return /<\/?[a-z][^>]*>/i.test(value);
}

function sanitizeMarkdownUrl(
  href: string | null | undefined,
  type: 'link' | 'image',
  imagePolicy: MarkdownImagePolicy = 'allow-relative-and-https',
): string | null {
  if (!href) return null;
  const value = href.trim();
  if (!value) return null;
  if (value.startsWith('#') || value.startsWith('?')) return value;
  if (type === 'link') {
    if (value.startsWith('/')) {
      return value.startsWith('//') ? null : value;
    }
    if (value.startsWith('./') || value.startsWith('../')) return value;
    if (/^[^\s/:?#]+(?:\/[^\s?#]*)?(?:\?[^\s#]*)?(?:#.*)?$/.test(value)) return value;
  }
  if (type === 'image' && imagePolicy === 'external-https-and-app-assets') {
    const appAssetPath = sanitizeAppAssetImagePath(value);
    if (appAssetPath) return appAssetPath;
  }

  if (type === 'image' && imagePolicy === 'allow-relative-and-https') {
    if (value.startsWith('/')) {
      return value.startsWith('//') ? null : value;
    }
    if (value.startsWith('./') || value.startsWith('../')) return value;
    if (/^[^\s/:?#]+(?:\/[^\s?#]*)?(?:\?[^\s#]*)?(?:#.*)?$/.test(value)) return value;
  }

  const schemeMatch = /^([a-zA-Z][a-zA-Z\d+.-]*):/.exec(value);
  if (!schemeMatch) return null;

  const scheme = schemeMatch[1].toLowerCase();
  if (type === 'image') {
    if (scheme === 'https') return value;
    if (
      scheme === 'http' &&
      imagePolicy === 'allow-relative-and-https' &&
      isLoopbackHttpUrl(value)
    ) {
      return value;
    }
    if (imagePolicy === 'allow-relative-and-https' && scheme === 'blob') {
      return value;
    }
    if (imagePolicy === 'allow-relative-and-https' && isSafeInlineDataImageUrl(value)) {
      return value;
    }
    return null;
  }

  const allowedSchemes = new Set(['https', 'mailto', 'tel']);
  return allowedSchemes.has(scheme) ? value : null;
}

function sanitizeAppAssetImagePath(value: string): string | null {
  if (value.startsWith('//') || value.includes('\\')) return null;
  if (!value.startsWith('/assets/') && !value.startsWith('assets/')) return null;

  try {
    const url = new URL(value, 'https://arsnova.local/');
    if (url.origin !== 'https://arsnova.local') return null;
    const decodedPath = decodeURIComponent(url.pathname);
    if (!decodedPath.startsWith('/assets/')) return null;
    if (decodedPath.split('/').some((segment) => segment === '.' || segment === '..')) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

function isLoopbackHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:') return false;

    const host = url.hostname.toLowerCase();
    return (
      host === 'localhost' ||
      host.endsWith('.localhost') ||
      host === '127.0.0.1' ||
      host === '0.0.0.0' ||
      host === '::1' ||
      host === '[::1]'
    );
  } catch {
    return false;
  }
}

function isExternalHttpsMarkdownUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' && !INTERNAL_MARKDOWN_LINK_HOSTNAMES.has(url.hostname.toLowerCase())
    );
  } catch {
    return false;
  }
}

/** App-Assets unter `/assets/…` oder absolutiert auf die Locale-Basis (PDF-Beispiele in MOTDs). */
function isAppAssetMarkdownUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('/assets/') || trimmed.startsWith('assets/')) return true;
  try {
    const url = new URL(trimmed);
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      /\/assets\//.test(url.pathname) &&
      (INTERNAL_MARKDOWN_LINK_HOSTNAMES.has(url.hostname.toLowerCase()) ||
        isLoopbackHttpUrl(trimmed) ||
        url.hostname.toLowerCase().endsWith('arsnova.eu'))
    );
  } catch {
    return false;
  }
}

function isSafeInlineDataImageUrl(value: string): boolean {
  return /^data:image\/(?:png|apng|avif|gif|jpeg|jpg|webp|bmp);base64,[a-z0-9+/=]+$/i.test(value);
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function sanitizeMarkdownHtml(html: string): string {
  // SSR: Auf dem Server existiert kein DOM. Unsere Render-Pipeline escaped inline HTML (renderer.html)
  // und erzwingt URL-Policies für Links/Bilder; DOMPurify wird daher nur im Browser angewendet.
  if (typeof window === 'undefined') return html;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'hr',
      'strong',
      'b',
      'em',
      'i',
      's',
      'code',
      'sub',
      'sup',
      'pre',
      'blockquote',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'div',
      'a',
      'button',
      'img',
      'span',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'svg',
      'path',
      // KaTeX (MathML + Annotation) – für A11y und Tests.
      'math',
      'semantics',
      'mrow',
      'mi',
      'mo',
      'mn',
      'msup',
      'msub',
      'mfrac',
      'msqrt',
      'mroot',
      'mtext',
      'mspace',
      'mtable',
      'mtr',
      'mtd',
      'mover',
      'munder',
      'munderover',
      'mstyle',
      'annotation',
    ],
    ALLOWED_ATTR: [
      'href',
      'title',
      'src',
      'alt',
      'type',
      'class',
      'target',
      'rel',
      'data-markdown-code-block',
      'data-markdown-code-copy',
      'data-markdown-copy-state',
      'data-markdown-image-lightbox',
      'loading',
      'decoding',
      'crossorigin',
      'referrerpolicy',
      'data-markdown-link-kind',
      'align',
      'aria-hidden',
      'aria-label',
      'disabled',
      'focusable',
      'viewBox',
      // KaTeX rendert Wurzelzeichen als SVG; ohne diese Geometrieattribute verschwindet das Radical.
      'width',
      'height',
      'preserveAspectRatio',
      'd',
      'fill',
      'stroke',
      'stroke-width',
      'stroke-linecap',
      'stroke-linejoin',
      'class',
      'target',
      'rel',
      'xmlns',
      'encoding',
      // KaTeX: Layout über tausende inline style=… auf span; ohne diese wirken Formeln „kaputt“/abgeschnitten.
      'style',
      // MathML: <math display="block"> …
      'display',
    ],
    // URL-Policies werden bereits vor dem HTML-Bau im Renderer erzwungen; hier müssen wir nur
    // verhindern, dass DOMPurify zulässige Preview-Bildquellen wie `blob:` nachträglich entfernt.
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto|tel|blob):|data:image\/(?:png|apng|avif|gif|jpeg|jpg|webp|bmp);base64,|[^a-z]|[a-z+.\-.]+(?:[^a-z+.\-:]|$))/i,
  });
}
